import React, { useState } from 'react';
import { X } from 'lucide-react';

interface ApprovalFormProps {
  stepId: number;
  stepTypeId: number;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

export default function ApprovalForm({ stepId, stepTypeId, onSubmit, onCancel }: ApprovalFormProps) {
  const [approval, setApproval] = useState({
    name: '',
    description: '',
    status: 'todo',
    priority: 'high',
    assigned_to: '',
    assigned_to_type: 'client',
    due_date: '',
    estimated_hours: 0,
    // Approval-specific fields
    approval_type: 'design', // design, content, legal, financial, final
    deliverable_link: '',
    approval_criteria: '',
    requires_signature: false,
    approval_deadline: '',
    escalation_contact: '',
    version_number: '1.0'
  });

  const handleSubmit = () => {
    if (!approval.name) {
      alert('Please enter an approval request name');
      return;
    }

    const config = {
      approval_type: approval.approval_type,
      deliverable_link: approval.deliverable_link,
      approval_criteria: approval.approval_criteria,
      requires_signature: approval.requires_signature,
      approval_deadline: approval.approval_deadline,
      escalation_contact: approval.escalation_contact,
      version_number: approval.version_number
    };

    onSubmit({
      step_type_id: stepTypeId,
      name: approval.name,
      description: approval.description,
      status: approval.status,
      priority: approval.priority,
      assigned_to: approval.assigned_to,
      assigned_to_type: approval.assigned_to_type,
      due_date: approval.due_date,
      estimated_hours: approval.estimated_hours,
      config: JSON.stringify(config)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Create Approval Request</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Approval Request Name *</label>
            <input
              type="text"
              value={approval.name}
              onChange={(e) => setApproval({ ...approval, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="e.g., Design Mockup Approval - Homepage"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Approval Type</label>
              <select
                value={approval.approval_type}
                onChange={(e) => setApproval({ ...approval, approval_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="design">Design</option>
                <option value="content">Content</option>
                <option value="legal">Legal</option>
                <option value="financial">Financial</option>
                <option value="final">Final Sign-off</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
              <input
                type="text"
                value={approval.version_number}
                onChange={(e) => setApproval({ ...approval, version_number: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="1.0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deliverable Link</label>
            <input
              type="url"
              value={approval.deliverable_link}
              onChange={(e) => setApproval({ ...approval, deliverable_link: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="https://..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Approval Criteria</label>
            <textarea
              value={approval.approval_criteria}
              onChange={(e) => setApproval({ ...approval, approval_criteria: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="What needs to be checked/approved..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Approver Name</label>
              <input
                type="text"
                value={approval.assigned_to}
                onChange={(e) => setApproval({ ...approval, assigned_to: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={approval.due_date}
                onChange={(e) => setApproval({ ...approval, due_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="requires_signature"
              checked={approval.requires_signature}
              onChange={(e) => setApproval({ ...approval, requires_signature: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="requires_signature" className="text-sm text-gray-700">
              Requires formal signature
            </label>
          </div>
        </div>

        <div className="flex gap-2 mt-6 pt-4 border-t">
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Create Approval Request
          </button>
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
